import axios, { AxiosResponse } from "axios";

/**
 * Shareable branded chart snapshots (ortex/web#1675).
 *
 * Measure keys the backend allows on public share cards (ORTEX-derived series
 * only — no vendor raw data). Keep in sync with shared_charts/constants.py.
 */
export const SHAREABLE_MEASURES = new Set(["sie", "sieff", "util", "ctb_avg_a", "ctb_min_a", "ctb_max_a", "dtc_sie_3m", "short_vol"]);

/**
 * Derive the backend measure key from a legend series URL.
 * URLs look like /interface/api/short/(tradingitem|security)/<id>/<family>/<data_key>/[<label>/]
 * and the measure key is `${data_key}` or `${data_key}_${label.toLowerCase()}`.
 */
export function measureKeyFromUrl(url: string): string | null {
    const segments = url.split("/").filter(Boolean);
    const idIdx = segments.findIndex((s) => s === "tradingitem" || s === "security");
    if (idIdx === -1 || segments.length < idIdx + 4) return null;
    const dataKey = segments[idIdx + 3];
    const label = segments[idIdx + 4];
    return label ? `${dataKey}_${label.toLowerCase()}` : dataKey;
}

export interface SharedChartCreateResponse {
    slug: string;
    url: string;
    imageUrl: string;
}

export interface SharedChartPublic {
    slug: string;
    ticker: string;
    exchangeSymbol: string;
    stockName: string;
    measures: { key: string; label: string }[];
    fromDate: string;
    toDate: string;
    createdAt: string;
    imageUrl: string;
    /** Values are {date, value} objects from the backend (dict keys camelized by the renderer). */
    headlineStats: Record<string, { date?: string; value?: number | null } | number | null>;
}

/** Create a share card. Returns the full response so callers can branch on 4xx (axios validateStatus < 500). */
export async function createSharedChart(payload: { tradingitem_id: number; measures: string[]; from_date: string; to_date: string }): Promise<AxiosResponse<SharedChartCreateResponse>> {
    const res = await axios.post<SharedChartCreateResponse>("/interface/api/shared_charts/", payload);
    return res;
}
